/**
 * FleetManager API Module Index
 *
 * This module provides a clean, modular API structure while maintaining
 * backward compatibility with the existing monolithic api.js.
 *
 * @module api
 *
 * Usage (new - recommended):
 *   import { vehicleAPI, driverAPI } from './api';
 *   import { authAPI } from './api/auth';
 *
 * Usage (legacy - still works):
 *   import api from './api';
 *   api.vehicle.getAll();
 */

// =============================================================================
// ALL IMPORTS AT TOP (ESLint requirement)
// =============================================================================

// Core
import {
  API_BASE_URL,
  MOBILE_API_BASE,
  getAuthHeaders,
  getMobileToken,
  apiRequest,
  mobileApiRequest,
  getDocumentUrl,
  buildQueryString,
} from './core.js';

// Auth
import { authAPI, superadminAPI } from './auth.js';

// Vehicles
import {
  vehicleAPI,
  vehicleDocumentAPI,
  vehicleInsuranceAPI,
  coverageModelAPI,
  vehicleExitAPI,
  vehicleAssignmentAPI,
  vehicleLocationAPI, // @deprecated - Use vehicleSiteAssignmentAPI
  vehicleSiteAssignmentAPI, // Site assignment history (vehicle_locations table)
  vehicleTelemetryAPI, // GPS tracking data (vehicle_telemetry table)
  vehicleHomeSiteAPI, // Home site (vehicles.working_site_id)
  vehicleResourceAPI,
  vehicleCatalogAPI,
  truckCatalogAPI,
} from './vehicles.js';

// Drivers
import { driverAPI, riskProfileAPI } from './drivers.js';

// Maintenance
import {
  maintenanceAPI,
  maintenanceCatalogAPI,
  tripAPI,
  inspectionAPI,
  inspectionItemAPI,
} from './maintenance.js';

// Claims
import { claimAPI, claimDocumentAPI } from './claims.js';

// Insurance
import {
  insuranceAPI,
  insuranceCoverageBlockAPI,
  insuranceModuleAPI,
  fleetInsuranceExportAPI,
} from './insurance.js';

// Reports
import {
  reportAPI,
  reportSubscriptionAPI,
  reportTemplateAPI,
  reportSeriesAPI,
  shareAPI,
  fleetCockpitExportAPI,
} from './reports.js';

// Admin
import {
  userAPI,
  userOrgAccessAPI,
  organizationAPI,
  workingSiteAPI,
  uiProfileAPI,
  widgetRegistryAPI,
  monitorAPI,
  sidebarAPI,
  featureTrackingAPI,
  featureSubtasksAPI,
  featureHealthAPI,
  onboardingAPI,
  accordionStateAPI,
  welcomeAPI,
  tourAPI,
  tenantArchiveAPI,
} from './admin.js';

// Operations
import {
  fuelLogAPI,
  fuelCardAPI,
  driverBadgeAPI,
  budgetAPI,
  tvsAPI,
  ctAPI,
  eventAPI,
  expenseAPI,
  expenseDocumentAPI,
  telematicsAlertAPI,
  telematicsAPI,
  kpiAPI,
  healthAPI,
  dossierAPI,
  aiPilotAPI,
} from './operations.js';

// Suppliers
import {
  supplierAPI,
  supplierServiceAPI,
  supplierMakeAPI,
  supplierContractAPI,
  workflowOutputAPI,
  workflowActionAPI,
} from './suppliers.js';

// Documents
import {
  documentAPI,
  documentValidationAPI,
  storageAPI,
} from './documents.js';

// Tools
import {
  toolAPI,
  toolSetAPI,
  toolSetItemAPI,
  toolDocumentAPI,
} from './tools.js';

// Fines
import { trafficFineAPI } from './fines.js';

// Mobile
import { mobileAPI } from './mobile.js';

// Pipeline
import { pipelineAPI } from './pipeline.js';

// Help
import { helpAPI } from './help.js';

// Guided Experience (step-by-step coaching)
import { guidedExperienceAPI } from './guided_experience.js';

// Campaigns (driver communications)
import { campaignAPI } from './campaigns.js';

// Missions (driver mono view + mission management)
import { driverMonoAPI, missionAPI } from './missions.js';

// Teams (equipes - operational team management)
import { teamAPI } from './teams.js';

// OCR (on-premise document text extraction)
import { ocrAPI } from './ocr.js';

// Calendar (autonomous event-driven calendar module)
import { calendarAPI } from './calendar.js';

// Pool (garde d'actif en self-service — module pilote ADR-010)
import { poolAPI } from './pool.js';
import { siteOrgAPI } from './siteOrg.js'; // lot 3.1
import { siteGrantsAPI } from './siteGrants.js'; // lot 3.2

// =============================================================================
// NAMED EXPORTS
// =============================================================================

// Core
export {
  API_BASE_URL,
  MOBILE_API_BASE,
  getAuthHeaders,
  getMobileToken,
  apiRequest,
  mobileApiRequest,
  getDocumentUrl,
  buildQueryString,
};

// Auth
export { authAPI, superadminAPI };

// Vehicles
export {
  vehicleAPI,
  vehicleDocumentAPI,
  vehicleInsuranceAPI,
  coverageModelAPI,
  vehicleExitAPI,
  vehicleAssignmentAPI,
  vehicleLocationAPI, // @deprecated - Use vehicleSiteAssignmentAPI
  vehicleSiteAssignmentAPI, // Site assignment history (vehicle_locations table)
  vehicleTelemetryAPI, // GPS tracking data (vehicle_telemetry table)
  vehicleHomeSiteAPI, // Home site (vehicles.working_site_id)
  vehicleResourceAPI,
  vehicleCatalogAPI,
  truckCatalogAPI,
};

// Drivers
export { driverAPI, riskProfileAPI };

// Maintenance
export {
  maintenanceAPI,
  maintenanceCatalogAPI,
  tripAPI,
  inspectionAPI,
  inspectionItemAPI,
};

// Claims
export { claimAPI, claimDocumentAPI };

// Insurance
export {
  insuranceAPI,
  insuranceCoverageBlockAPI,
  insuranceModuleAPI,
  fleetInsuranceExportAPI,
};

// Reports
export {
  reportAPI,
  reportSubscriptionAPI,
  reportTemplateAPI,
  reportSeriesAPI,
  shareAPI,
  fleetCockpitExportAPI,
};

// Admin
export {
  userAPI,
  userOrgAccessAPI,
  organizationAPI,
  workingSiteAPI,
  uiProfileAPI,
  widgetRegistryAPI,
  monitorAPI,
  sidebarAPI,
  featureTrackingAPI,
  featureSubtasksAPI,
  featureHealthAPI,
  onboardingAPI,
  accordionStateAPI,
  welcomeAPI,
  tourAPI,
  tenantArchiveAPI,
};

// Operations
export {
  fuelLogAPI,
  fuelCardAPI,
  driverBadgeAPI,
  budgetAPI,
  tvsAPI,
  ctAPI,
  eventAPI,
  expenseAPI,
  expenseDocumentAPI,
  telematicsAlertAPI,
  telematicsAPI,
  kpiAPI,
  healthAPI,
  dossierAPI,
  aiPilotAPI,
};

// Suppliers
export {
  supplierAPI,
  supplierServiceAPI,
  supplierMakeAPI,
  supplierContractAPI,
  workflowOutputAPI,
  workflowActionAPI,
};

// Documents
export {
  documentAPI,
  documentValidationAPI,
  storageAPI,
};

// Tools
export {
  toolAPI,
  toolSetAPI,
  toolSetItemAPI,
  toolDocumentAPI,
};

// Fines
export { trafficFineAPI };

// Mobile
export { mobileAPI };

// Pipeline
export { pipelineAPI };

// Help
export { helpAPI };

// Guided Experience
export { guidedExperienceAPI };

// Campaigns
export { campaignAPI };

// Missions
export { driverMonoAPI, missionAPI };

// Teams
export { teamAPI };

// OCR
export { ocrAPI };

// Calendar
export { calendarAPI };

// Pool
export { poolAPI };
export { siteOrgAPI };
export { siteGrantsAPI };

// =============================================================================
// BACKWARD COMPATIBLE DEFAULT EXPORT
// =============================================================================

/**
 * Default export for backward compatibility
 * Matches the original api.js default export structure
 */
const api = {
  // Vehicles
  vehicle: vehicleAPI,
  vehicleDocument: vehicleDocumentAPI,
  vehicleInsurance: vehicleInsuranceAPI,
  coverageModel: coverageModelAPI,
  vehicleExit: vehicleExitAPI,
  vehicleAssignment: vehicleAssignmentAPI,
  vehicleLocation: vehicleLocationAPI, // @deprecated - Use vehicleSiteAssignment
  vehicleSiteAssignment: vehicleSiteAssignmentAPI, // Site assignment history
  vehicleTelemetry: vehicleTelemetryAPI, // GPS tracking data
  vehicleHomeSite: vehicleHomeSiteAPI, // Home site
  vehicleResource: vehicleResourceAPI,
  vehicleCatalog: vehicleCatalogAPI,
  truckCatalog: truckCatalogAPI,

  // Drivers
  driver: driverAPI,
  riskProfile: riskProfileAPI,

  // Maintenance
  maintenance: maintenanceAPI,
  maintenanceCatalog: maintenanceCatalogAPI,
  trip: tripAPI,
  inspection: inspectionAPI,
  inspectionItem: inspectionItemAPI,

  // Claims
  claim: claimAPI,
  claimDocument: claimDocumentAPI,

  // Insurance
  insurance: insuranceAPI,
  insurance_contract: insuranceAPI, // Alias for consistency
  insurance_coverage_block: insuranceCoverageBlockAPI,
  insuranceModule: insuranceModuleAPI,
  fleetInsuranceExport: fleetInsuranceExportAPI,

  // Reports
  report: reportAPI,
  reportSubscription: reportSubscriptionAPI,
  reportTemplate: reportTemplateAPI,
  reportSeries: reportSeriesAPI,
  share: shareAPI,
  fleetCockpitExport: fleetCockpitExportAPI,

  // Admin
  user: userAPI,
  userOrgAccess: userOrgAccessAPI,
  organization: organizationAPI,
  workingSite: workingSiteAPI,
  uiProfile: uiProfileAPI,
  widgetRegistry: widgetRegistryAPI,
  monitor: monitorAPI,
  sidebar: sidebarAPI,
  featureTracking: featureTrackingAPI,
  featureSubtasks: featureSubtasksAPI,
  featureHealth: featureHealthAPI,
  onboarding: onboardingAPI,
  accordionState: accordionStateAPI,
  welcome: welcomeAPI,
  tour: tourAPI,

  // Operations
  fuelLog: fuelLogAPI,
  fuelCard: fuelCardAPI,
  driverBadge: driverBadgeAPI,
  budget: budgetAPI,
  tvs: tvsAPI,
  ct: ctAPI,
  event: eventAPI,
  expense: expenseAPI,
  expenseDocument: expenseDocumentAPI,
  telematicsAlert: telematicsAlertAPI,
  telematics: telematicsAPI,
  kpi: kpiAPI,
  health: healthAPI,
  dossier: dossierAPI,
  aiPilot: aiPilotAPI,

  // Suppliers
  supplier: supplierAPI,
  supplierService: supplierServiceAPI,
  supplierMake: supplierMakeAPI,
  supplierContract: supplierContractAPI,
  workflowOutput: workflowOutputAPI,
  workflowAction: workflowActionAPI,

  // Documents
  document: documentAPI,
  documentValidation: documentValidationAPI,
  storage: storageAPI,

  // Tools
  tool: toolAPI,
  toolSet: toolSetAPI,
  toolSetItem: toolSetItemAPI,
  toolDocument: toolDocumentAPI,

  // Fines
  trafficFine: trafficFineAPI,

  // Auth
  auth: authAPI,
  superadmin: superadminAPI,

  // Pipeline
  pipeline: pipelineAPI,

  // Campaigns
  campaign: campaignAPI,

  // Missions
  driverMono: driverMonoAPI,
  mission: missionAPI,

  // Teams
  team: teamAPI,

  // Calendar
  calendar: calendarAPI,

  // Pool
  pool: poolAPI,

  // Utility functions
  getDocumentUrl,

  // Generic entity access (for dynamic entity names like insurance_company, guarantee_template, etc.)
  getAll: (entityName, params = {}) => {
    const queryString = buildQueryString(params);
    return apiRequest(`/${entityName}${queryString}`);
  },

  getById: (entityName, id) => {
    return apiRequest(`/${entityName}/${id}`);
  },

  create: (entityName, data) => {
    return apiRequest(`/${entityName}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: (entityName, id, data) => {
    return apiRequest(`/${entityName}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: (entityName, id) => {
    return apiRequest(`/${entityName}/${id}`, {
      method: 'DELETE',
    });
  },
};

export default api;
